import { useMarked } from "../context/marked"
import { useI18n } from "../context/i18n"
import { useChatLinkTitles, type ChatLinkTarget, type ChatLinkTitleResolver } from "../context/chat-link-titles"
import { useArtifactPaths, type ArtifactPathResolver } from "../context/artifact-paths"
import DOMPurify from "dompurify"
import morphdom from "morphdom"
import { checksum } from "@opencode-ai/core/util/encode"
import { fileUrlFromPath, isAbsolutePath } from "@opencode-ai/core/util/path"
import { ComponentProps, createEffect, createResource, createSignal, onCleanup, splitProps } from "solid-js"
import { isServer } from "solid-js/web"
import { stream } from "./markdown-stream"
import { artifactFileAccent, chooseIconName, colorWithAlpha, getFileIconUrl } from "./file-icon"

type Entry = {
  hash: string
  html: string
}

const max = 200
const cache = new Map<string, Entry>()

if (typeof window !== "undefined" && DOMPurify.isSupported) {
  DOMPurify.addHook("afterSanitizeAttributes", (node: Element) => {
    if (!(node instanceof HTMLAnchorElement)) return
    if (node.target !== "_blank") return

    const rel = node.getAttribute("rel") ?? ""
    const set = new Set(rel.split(/\s+/).filter(Boolean))
    set.add("noopener")
    set.add("noreferrer")
    node.setAttribute("rel", Array.from(set).join(" "))
  })
}

const config = {
  USE_PROFILES: { html: true, mathMl: true },
  SANITIZE_NAMED_PROPS: true,
  FORBID_TAGS: ["style"],
  FORBID_CONTENTS: ["style", "script"],
}

const iconPaths = {
  copy: '<path d="M6.2513 6.24935V2.91602H17.0846V13.7493H13.7513M13.7513 6.24935V17.0827H2.91797V6.24935H13.7513Z" stroke="currentColor" stroke-linecap="round"/>',
  check: '<path d="M5 11.9657L8.37838 14.7529L15 5.83398" stroke="currentColor" stroke-linecap="square"/>',
}

function sanitize(html: string) {
  if (!DOMPurify.isSupported) return ""
  return DOMPurify.sanitize(html, config)
}

function escape(text: string) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function fallback(markdown: string) {
  return escape(markdown).replace(/\r\n?/g, "\n").replace(/\n/g, "<br>")
}

type CopyLabels = {
  copy: string
  copied: string
}

const urlPattern = /^https?:\/\/[^\s<>()`"']+$/
const localPathStartPattern = /\/(?:Users|Volumes|Applications|Library|System|private|tmp|var|opt|usr|etc|home)\//
const localPathLabelPattern = /\s+\p{Lu}[\p{Ll}\d_.-]*(?:\s+[\p{Ll}\d_.-]+)*:\s*$/u

function basenameOfPath(path: string) {
  return path.replace(/\\/g, "/").split("/").filter(Boolean).pop() ?? path
}

function codeUrl(text: string) {
  const href = text.trim().replace(/[),.;!?]+$/, "")
  if (!urlPattern.test(href)) return
  try {
    const url = new URL(href)
    return url.toString()
  } catch {
    return
  }
}

function createIcon(path: string, slot: string) {
  const icon = document.createElement("div")
  icon.setAttribute("data-component", "icon")
  icon.setAttribute("data-size", "small")
  icon.setAttribute("data-slot", slot)
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("data-slot", "icon-svg")
  svg.setAttribute("fill", "none")
  svg.setAttribute("viewBox", "0 0 20 20")
  svg.setAttribute("aria-hidden", "true")
  svg.innerHTML = path
  icon.appendChild(svg)
  return icon
}

function createCopyButton(labels: CopyLabels) {
  const button = document.createElement("button")
  button.type = "button"
  button.setAttribute("data-component", "icon-button")
  button.setAttribute("data-variant", "secondary")
  button.setAttribute("data-size", "small")
  button.setAttribute("data-slot", "markdown-copy-button")
  button.setAttribute("aria-label", labels.copy)
  button.appendChild(createIcon(iconPaths.copy, "copy-icon"))
  button.appendChild(createIcon(iconPaths.check, "check-icon"))
  return button
}

function setCopyState(button: HTMLButtonElement, labels: CopyLabels, copied: boolean) {
  if (copied) {
    button.setAttribute("data-copied", "true")
    button.setAttribute("aria-label", labels.copied)
    return
  }
  button.removeAttribute("data-copied")
  button.setAttribute("aria-label", labels.copy)
}

function ensureCodeWrapper(block: HTMLPreElement, labels: CopyLabels) {
  const parent = block.parentElement
  if (!parent) return
  const wrapped = parent.getAttribute("data-component") === "markdown-code"
  if (!wrapped) {
    const wrapper = document.createElement("div")
    wrapper.setAttribute("data-component", "markdown-code")
    parent.replaceChild(wrapper, block)
    wrapper.appendChild(block)
    wrapper.appendChild(createCopyButton(labels))
    return
  }

  const buttons = Array.from(parent.querySelectorAll('[data-slot="markdown-copy-button"]')).filter(
    (el): el is HTMLButtonElement => el instanceof HTMLButtonElement,
  )

  if (buttons.length === 0) {
    parent.appendChild(createCopyButton(labels))
    return
  }

  for (const button of buttons.slice(1)) {
    button.remove()
  }
}

function wrapMarkdownTables(root: HTMLDivElement) {
  const tables = Array.from(root.querySelectorAll("table"))
  for (const table of tables) {
    if (table.closest('[data-component="tma-table"]')) continue
    const parent = table.parentElement
    if (!parent) continue

    const wrap = document.createElement("div")
    wrap.setAttribute("data-component", "tma-table")
    const scroll = document.createElement("div")
    scroll.setAttribute("data-slot", "tma-table-scroll")

    parent.replaceChild(wrap, table)
    wrap.appendChild(scroll)
    scroll.appendChild(table)
  }
}

function markCodeLinks(root: HTMLDivElement) {
  const codeNodes = Array.from(root.querySelectorAll(":not(pre) > code"))
  for (const code of codeNodes) {
    const href = codeUrl(code.textContent ?? "")
    const parentLink =
      code.parentElement instanceof HTMLAnchorElement && code.parentElement.classList.contains("external-link")
        ? code.parentElement
        : null

    if (!href) {
      if (parentLink) parentLink.replaceWith(code)
      continue
    }

    if (parentLink) {
      parentLink.href = href
      continue
    }

    const link = document.createElement("a")
    link.href = href
    link.className = "external-link"
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    code.parentNode?.replaceChild(link, code)
    link.appendChild(code)
  }
}

export function localPathRanges(text: string) {
  const ranges: Array<{ start: number; end: number; path: string }> = []
  let offset = 0

  while (offset < text.length) {
    const startMatch = localPathStartPattern.exec(text.slice(offset))
    if (!startMatch) return ranges

    const start = offset + startMatch.index
    const nextLine = text.indexOf("\n", start)
    const nextPathMatch = localPathStartPattern.exec(text.slice(start + 1))
    const rawEnd = Math.min(
      nextLine === -1 ? text.length : nextLine,
      nextPathMatch ? start + 1 + nextPathMatch.index : text.length,
    )
    const raw = text.slice(start, rawEnd)
    const trimmedStart = raw.search(/\S/)
    const value = raw
      .slice(trimmedStart === -1 ? 0 : trimmedStart)
      .replace(localPathLabelPattern, "")
      .trim()
      // Sentence punctuation glued to the path — «сохранена как /Users/…/deck.pptx.»
      // — must not become part of the path/chip label (same set codeUrl strips).
      .replace(/[),.;!?]+$/, "")
    if (!value) {
      offset = rawEnd + 1
      continue
    }

    ranges.push({ start, end: start + value.length, path: value })
    offset = rawEnd
  }

  return ranges
}

function styleArtifactLink(link: HTMLAnchorElement, filename: string) {
  link.classList.add("gigatool-artifact-link", "artifact-link-with-icon")
  const color = artifactFileAccent(filename)
  link.style.setProperty("--artifact-link-color", color)
  link.style.setProperty("--artifact-link-background", colorWithAlpha(color, 0.1))
}

function appendArtifactLinkLabel(link: HTMLAnchorElement, filename: string, code?: HTMLElement) {
  styleArtifactLink(link, filename)
  link.appendChild(createArtifactFileIcon(filename))
  if (code) {
    code.textContent = filename
    link.appendChild(code)
    return
  }
  const label = document.createElement("code")
  label.textContent = filename
  link.appendChild(label)
}

function linkLocalPaths(root: HTMLDivElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest("a, pre, code")) return NodeFilter.FILTER_REJECT
      if (!localPathStartPattern.test(node.textContent ?? "")) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []

  while (true) {
    const node = walker.nextNode()
    if (!node) break
    nodes.push(node as Text)
  }

  for (const node of nodes) {
    const text = node.textContent ?? ""
    const ranges = localPathRanges(text)
    if (ranges.length === 0) continue

    const fragment = document.createDocumentFragment()
    let cursor = 0
    for (const range of ranges) {
      if (range.start > cursor) fragment.append(text.slice(cursor, range.start))

      const link = document.createElement("a")
      link.href = `file://${encodeURI(range.path)}`
      link.className = "external-link"
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.dataset.localPath = range.path
      link.title = range.path
      appendArtifactLinkLabel(link, basenameOfPath(range.path))
      fragment.append(link)
      cursor = range.end
    }
    if (cursor < text.length) fragment.append(text.slice(cursor))
    node.replaceWith(fragment)
  }
}

// Returns the absolute local path that a bare inline-code string represents
// (the whole code IS a path, e.g. `/Users/me/deck.pptx`), or undefined when
// the code merely mentions one mid-string (e.g. `python /Users/me/run.py`,
// which we don't want to make wholly clickable) or isn't a path at all.
export function codeLocalPath(text: string): string | undefined {
  const trimmed = text.trim()
  if (!localPathStartPattern.test(trimmed)) return
  const range = localPathRanges(trimmed)[0]
  if (!range || range.start !== 0) return
  return range.path
}

// GigaTool: models often wrap an artifact path in inline code — e.g.
// "Файл: `/Users/.../vidy-irisov.pptx`" — which markdown renders as a
// <code> element. linkLocalPaths walks text nodes and deliberately skips
// anything inside <code>/<pre>, so those paths never become clickable. Catch
// that case here: when an inline <code> element's content *is* an absolute
// local path, wrap the whole element in a file:// link (same scheme as
// linkLocalPaths) so the renderer's global click delegate reveals it in
// Finder, exactly like a bare path in prose.
function linkLocalPathCode(root: HTMLDivElement) {
  const codes = Array.from(root.querySelectorAll("code"))
  for (const code of codes) {
    // Skip block-level code (inside <pre>) and already-wrapped code.
    if (code.parentElement instanceof HTMLPreElement) continue
    if (code.parentElement instanceof HTMLAnchorElement) continue
    const path = codeLocalPath(code.textContent ?? "")
    if (!path) continue
    const a = document.createElement("a")
    a.href = `file://${encodeURI(path)}`
    a.className = "external-link"
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    a.dataset.localPath = path
    a.title = path
    code.parentNode?.insertBefore(a, code)
    appendArtifactLinkLabel(a, basenameOfPath(path), code)
  }
}

export function decorate(
  root: HTMLDivElement,
  labels: CopyLabels,
  opts: {
    linkArtifacts: boolean
    artifactFilenames?: Set<string>
    resolveArtifactPath?: ArtifactPathResolver
    chatLinkLabel: string
    resolveChatTitle?: ChatLinkTitleResolver
  },
) {
  const blocks = Array.from(root.querySelectorAll("pre"))
  for (const block of blocks) {
    ensureCodeWrapper(block, labels)
  }
  wrapMarkdownTables(root)
  markCodeLinks(root)
  // Chat-session links MUST be linkified before linkLocalPaths: a chat link's
  // directory param is an absolute path (/Users/…, /home/…), so linkLocalPaths
  // would otherwise greedily wrap the URL's tail into a file:// anchor and
  // split the text node before linkChatSessions ever sees the intact URL.
  // Once wrapped in an <a>, linkLocalPaths skips it (it rejects nodes under a/pre/code).
  linkChatSessions(root, opts.chatLinkLabel, opts.resolveChatTitle)
  linkLocalPaths(root)
  linkLocalPathCode(root)
  // Plan-mode messages don't actually create files, so we skip turning
  // inline-code filenames into clickable artifact links — the resolution
  // would fail and the click would feel broken.
  if (opts.linkArtifacts) linkArtifactFilenames(root, opts.artifactFilenames, opts.resolveArtifactPath)
}

// GigaTool: chat deep links (`gigatool://open-session?directory=…&session=…`,
// legacy `opencode://` scheme) pasted into a message would otherwise render as
// an opaque wall of URL text. Sanitization strips anchors with custom schemes,
// so this post-pass rebuilds them: the raw URL becomes a compact labelled link
// ("Chat · <folder>") that the renderer's global click delegate routes back
// into the app as a deep link.
export const chatSessionLinkPattern = /(?:gigatool|opencode):\/\/open-session\?[^\s<>()"'`]+/g

export function chatSessionLinkTarget(href: string): ChatLinkTarget {
  try {
    const params = new URL(href).searchParams
    return {
      directory: params.get("directory") ?? undefined,
      session: params.get("session") ?? undefined,
    }
  } catch {
    return {}
  }
}

export function chatSessionLinkText(href: string, label: string, resolveTitle?: ChatLinkTitleResolver) {
  const target = chatSessionLinkTarget(href)
  const title = resolveTitle?.(target)?.trim()
  if (title) return `${label} · ${title}`
  const basename = target.directory?.split(/[\\/]/).filter(Boolean).at(-1)
  if (!basename) return label
  // Auto-created home-chat folders are named Chat-N, so the default label
  // would read as a stutter («Чат · Chat-263»). Collapse it to the localized
  // prefix + number instead.
  const generic = basename.match(/^chat[-_ ]?(\d+)$/i)
  if (generic) return `${label}-${generic[1]}`
  return `${label} · ${basename}`
}

function linkChatSessions(root: HTMLDivElement, label: string, resolveTitle?: ChatLinkTitleResolver) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest("a, pre, code")) return NodeFilter.FILTER_REJECT
      if (!/(?:gigatool|opencode):\/\/open-session\?/.test(node.textContent ?? "")) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []

  while (true) {
    const node = walker.nextNode()
    if (!node) break
    nodes.push(node as Text)
  }

  for (const node of nodes) {
    const text = node.textContent ?? ""
    const matches = Array.from(text.matchAll(chatSessionLinkPattern))
    if (matches.length === 0) continue

    const fragment = document.createDocumentFragment()
    let cursor = 0
    for (const match of matches) {
      if (match.index > cursor) fragment.append(text.slice(cursor, match.index))

      const href = match[0].replace(/[),.;!?]+$/, "")
      const link = document.createElement("a")
      link.href = href
      link.className = "external-link chat-session-link"
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.textContent = chatSessionLinkText(href, label, resolveTitle)
      fragment.append(link)
      cursor = match.index + href.length
    }
    if (cursor < text.length) fragment.append(text.slice(cursor))
    node.replaceWith(fragment)
  }
}

// GigaTool: turn inline `<code>` elements whose text is a bare artifact
// filename (e.g. `Шаблон_БЖУ.xlsx`) into clickable links. We don't know the
// full path here — that's resolved later by a session-level listener that
// scans recent tool-write parts. The href uses a synthetic
// gigatool-artifact:// scheme so the renderer's global click delegate can
// recognise these and dispatch a custom event for resolution.
//
// Extensions allowlist matches packages/app/src/components/file-preview/util.ts.
const ARTIFACT_FILENAME_PATTERN =
  /^[\wЀ-ӿ.\-]+\.(json|py|pdf|csv|tsv|xlsx?|pptx?|docx?|md|markdown|html?|png|jpe?g|gif|webp)$/i

let artifactIconId = 0

function createArtifactFileIcon(filename: string) {
  const id = `artifact-file-icon-${artifactIconId++}`
  const source = getFileIconUrl(chooseIconName(filename, "file", false))
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("data-component", "file-icon")
  svg.setAttribute("class", "artifact-link-icon")
  svg.setAttribute("aria-hidden", "true")
  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
  const mask = document.createElementNS("http://www.w3.org/2000/svg", "mask")
  const image = document.createElementNS("http://www.w3.org/2000/svg", "image")
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  mask.setAttribute("id", id)
  mask.setAttribute("mask-type", "alpha")
  image.setAttribute("href", source)
  image.setAttribute("width", "100%")
  image.setAttribute("height", "100%")
  image.setAttribute("preserveAspectRatio", "xMidYMid meet")
  rect.setAttribute("width", "100%")
  rect.setAttribute("height", "100%")
  rect.setAttribute("fill", "currentColor")
  rect.setAttribute("mask", `url(#${id})`)
  mask.appendChild(image)
  defs.appendChild(mask)
  svg.appendChild(defs)
  svg.appendChild(rect)
  return svg
}

function linkArtifactFilenames(root: HTMLDivElement, knownFiles?: Set<string>, resolvePath?: ArtifactPathResolver) {
  const codes = Array.from(root.querySelectorAll("code"))
  for (const code of codes) {
    // Skip block-level code (inside <pre>) and already-wrapped code.
    if (code.parentElement instanceof HTMLPreElement) continue
    if (code.parentElement instanceof HTMLAnchorElement) continue
    const text = (code.textContent ?? "").trim()
    // Two independent signals that an inline-code token is an openable file:
    //  1. its extension is one we recognise as a document/data/image, OR
    //  2. it's the exact basename of a file the model wrote/edited this turn
    //     (knownFiles). This catches source files like `deck.js` whose
    //     extension is deliberately kept OUT of the static allowlist — adding
    //     `js`/`ts`/… there would also linkify prose like `Node.js`, which
    //     resolves to nothing. Keying off files we know were produced makes
    //     any extension safe and guarantees the click resolves.
    if (!ARTIFACT_FILENAME_PATTERN.test(text) && !knownFiles?.has(text)) continue
    const a = document.createElement("a")
    // When the host can resolve the basename to an absolute path (a file the
    // model just wrote), emit a real file:// chip: it gets the full OS context
    // menu (Open / Show in Folder / Copy path) and reveals on click, identical
    // to an absolute-path chip in prose. Otherwise fall back to the synthetic
    // gigatool-artifact:// scheme, which the global click delegate resolves
    // lazily. encodeURI (not encodeURIComponent) matches how the delegate and
    // the main-process menu decode file:// hrefs (decodeURI of the tail).
    const resolved = resolvePath?.(text)
    if (resolved && isAbsolutePath(resolved)) {
      // fileUrlFromPath (not a bare encodeURI): a Windows path is backslash-
      // separated, so encodeURI alone leaves zero `/` after file:// — an
      // unusable URL. This emits file:///C:/… and round-trips through the
      // desktop click delegate's decodeURI.
      a.href = fileUrlFromPath(resolved)
      a.dataset.localPath = resolved
    } else {
      a.href = `gigatool-artifact://${encodeURIComponent(text)}`
    }
    // .external-link routes the click through the renderer's global delegate
    // (packages/desktop-electron/src/renderer/index.tsx::handleClick), which
    // looks at the URL scheme. .gigatool-artifact-link is a marker we can
    // style or query independently if needed.
    a.className = "external-link gigatool-artifact-link artifact-link-with-icon"
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    a.dataset.artifactFilename = text
    code.parentNode?.insertBefore(a, code)
    appendArtifactLinkLabel(a, text, code)
  }
}

function setupCodeCopy(root: HTMLDivElement, getLabels: () => CopyLabels) {
  const timeouts = new Map<HTMLButtonElement, ReturnType<typeof setTimeout>>()

  const updateLabel = (button: HTMLButtonElement) => {
    const labels = getLabels()
    const copied = button.getAttribute("data-copied") === "true"
    setCopyState(button, labels, copied)
  }

  const handleClick = async (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof Element)) return

    const button = target.closest('[data-slot="markdown-copy-button"]')
    if (!(button instanceof HTMLButtonElement)) return
    const code = button.closest('[data-component="markdown-code"]')?.querySelector("code")
    const content = code?.textContent ?? ""
    if (!content) return
    const clipboard = navigator?.clipboard
    if (!clipboard) return
    await clipboard.writeText(content)
    const labels = getLabels()
    setCopyState(button, labels, true)
    const existing = timeouts.get(button)
    if (existing) clearTimeout(existing)
    const timeout = setTimeout(() => setCopyState(button, labels, false), 2000)
    timeouts.set(button, timeout)
  }

  const buttons = Array.from(root.querySelectorAll('[data-slot="markdown-copy-button"]'))
  for (const button of buttons) {
    if (button instanceof HTMLButtonElement) updateLabel(button)
  }

  root.addEventListener("click", handleClick)

  return () => {
    root.removeEventListener("click", handleClick)
    for (const timeout of timeouts.values()) {
      clearTimeout(timeout)
    }
  }
}

function touch(key: string, value: Entry) {
  cache.delete(key)
  cache.set(key, value)

  if (cache.size <= max) return

  const first = cache.keys().next().value
  if (!first) return
  cache.delete(first)
}

export function Markdown(
  props: ComponentProps<"div"> & {
    text: string
    cacheKey?: string
    streaming?: boolean
    class?: string
    classList?: Record<string, boolean>
    // GigaTool: turn off the artifact-filename anchoring for messages where
    // no files are actually produced (plan mode). Defaults to true so the
    // existing call sites (assistant messages in build/ask/etc.) keep
    // working unchanged.
    linkArtifacts?: boolean
    // GigaTool: basenames of files the model wrote/edited this turn. An
    // inline-code token matching one of these is linkified regardless of its
    // extension (see linkArtifactFilenames), so source files like `deck.js`
    // become clickable while prose like `Node.js` does not.
    artifactFilenames?: string[]
  },
) {
  const [local, others] = splitProps(props, [
    "text",
    "cacheKey",
    "streaming",
    "class",
    "classList",
    "linkArtifacts",
    "artifactFilenames",
  ])
  const marked = useMarked()
  const i18n = useI18n()
  const resolveChatTitle = useChatLinkTitles()
  const resolveArtifactPath = useArtifactPaths()
  const [root, setRoot] = createSignal<HTMLDivElement>()
  const [html] = createResource(
    () => ({
      text: local.text,
      key: local.cacheKey,
      streaming: local.streaming ?? false,
    }),
    async (src) => {
      if (isServer) return fallback(src.text)
      if (!src.text) return ""

      const base = src.key ?? checksum(src.text)
      return Promise.all(
        stream(src.text, src.streaming).map(async (block, index) => {
          const hash = checksum(block.raw)
          const key = base ? `${base}:${index}:${block.mode}` : hash

          if (key && hash) {
            const cached = cache.get(key)
            if (cached && cached.hash === hash) {
              touch(key, cached)
              return cached.html
            }
          }

          const next = await Promise.resolve(marked.parse(block.src))
          const safe = sanitize(next)
          if (key && hash) touch(key, { hash, html: safe })
          return safe
        }),
      )
        .then((list) => list.join(""))
        .catch(() => fallback(src.text))
    },
    { initialValue: fallback(local.text) },
  )

  let copyCleanup: (() => void) | undefined

  createEffect(() => {
    const container = root()
    const content = local.text ? (html.latest ?? html() ?? "") : ""
    if (!container) return
    if (isServer) return

    if (!content) {
      container.innerHTML = ""
      return
    }

    const labels = {
      copy: i18n.t("ui.message.copy"),
      copied: i18n.t("ui.message.copied"),
    }
    const temp = document.createElement("div")
    temp.innerHTML = content
    const known = local.artifactFilenames
    decorate(temp, labels, {
      linkArtifacts: local.linkArtifacts ?? true,
      artifactFilenames: known && known.length ? new Set(known) : undefined,
      resolveArtifactPath,
      chatLinkLabel: i18n.t("ui.message.chatLink"),
      resolveChatTitle,
    })

    morphdom(container, temp, {
      childrenOnly: true,
      onBeforeElUpdated: (fromEl, toEl) => {
        if (
          fromEl instanceof HTMLButtonElement &&
          toEl instanceof HTMLButtonElement &&
          fromEl.getAttribute("data-slot") === "markdown-copy-button" &&
          toEl.getAttribute("data-slot") === "markdown-copy-button" &&
          fromEl.getAttribute("data-copied") === "true"
        ) {
          setCopyState(toEl, labels, true)
        }
        if (fromEl.isEqualNode(toEl)) return false
        return true
      },
    })

    if (!copyCleanup)
      copyCleanup = setupCodeCopy(container, () => ({
        copy: i18n.t("ui.message.copy"),
        copied: i18n.t("ui.message.copied"),
      }))
  })

  onCleanup(() => {
    if (copyCleanup) copyCleanup()
  })

  return (
    <div
      data-component="markdown"
      classList={{
        ...local.classList,
        [local.class ?? ""]: !!local.class,
      }}
      ref={setRoot}
      {...others}
    />
  )
}
